import { HttpClient } from '@angular/common/http';
import { AbstractModel } from './abstract.model';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {ServiceAnswer} from './service-answer';
import { Observable } from 'rxjs';
import { retryBackoff, RetryBackoffConfig } from 'backoff-rxjs';

/**
 * CRUD service for manipulating and reading database.
 * TODO this service should broadcast error message if http call fails.
 */
@Injectable()
export abstract class AbstractCrudService<T extends AbstractModel> {

  /**
   * URL for current model
   * @type {string}
   */
  protected url = environment.apiURL + 'test';

  /**
   * Default values used for `listWithRetry` and `filterWithRetry`
   * @type {RetryBackoffConfig}
   */
  protected defaultRetryConfig: RetryBackoffConfig = {
    initialInterval: 1_000,
    maxInterval: 60_000,
    maxRetries: 4
  };

  /**
   * Default constructor
   * @param {HttpClient} http - Angular Http Client
   */
  protected constructor(protected http: HttpClient) {
  }

  /**
   * Create new data
   * @param {T} data
   * @returns {Promise<T>}
   */
  public async create(data: T): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.post<T>(this.url, data).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Fetch single data with ID
   * @param {number} id
   * @returns {Promise<T>}
   */
  public async get(id: number | string): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.get<T>(this.url + '/' + id).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Fetch all rows from backend
   * @returns {Promise<T[]>}
   */
  public async list(): Promise<ServiceAnswer<T[]>> {
    try {
      return this.answerFromSuccess(await this.http.get<T[]>(this.url + '?withDeleted=false').toPromise())
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Fetch all rows from backend, retrying if the request fails
   * @param {RetryBackoffConfig} [retryConfig]
   * @returns {Observable<T[]>}
   */
  public listWithRetry(retryConfig?: RetryBackoffConfig): Observable<T[]> {
    return this.http.get<T[]>(this.url + '?withDeleted=false').pipe(
      retryBackoff({
        ...this.defaultRetryConfig,
        ...retryConfig,
      })
    );
  }

  /**
   * Update existing data
   * @param {T} data
   * @returns {Promise<ServiceAnswer<T>>}
   */
  public async edit(data: T): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.put<T>(this.url + '/' + data.id, data).toPromise())
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Restore existing removed data
   * @param {T} data
   * @returns {Promise<ServiceAnswer<T>>}
   */
  public async restore(data: T): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.patch<T>(this.url + '/' + data.id, {deletedAt: ''}).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Remove existing data (soft delete)
   * @param {T} data
   * @returns {Promise<ServiceAnswer<T>>}
   */
  public async remove(data: T): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.patch<T>(this.url + '/' + data.id, {deletedAt: new Date()}).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Destroy existing data (hard delete)
   * @param {T} data
   * @returns {Promise<ServiceAnswer<T>>}
   */
  public async destroy(data: T): Promise<ServiceAnswer<T>> {
    try {
      return this.answerFromSuccess(await this.http.delete<T>(this.url + '/' + data.id).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Fetch rows matching a given query
   * @param {object} query
   * @param {string} [mode=and]
   * @returns {Promise<T[]>}
   */
  public async filter(query: object, mode: 'and' | 'or' = 'and'): Promise<ServiceAnswer<T[]>> {
/*    console.log('Filter with', query);
    console.log('Filter mode', mode);*/
    let params = '';
    for (const entry of Object.entries(query)) {
      params = params + entry[0] + '=' + entry[1] + (mode === 'and' ? '&' : '|');
    }
    if (params.length !== 0) {
      params = params.substring(0, params.length - 1);
    }

    try {
      return this.answerFromSuccess(await this.http.get<T[]>(this.url + '?' + params).toPromise());
    } catch (error) {
      return this.answerFromError(error);
    }
  }

  /**
   * Fetch rows matching a given query, retrying it the request fails
   * @param {object} query
   * @param {string} [mode=and]
   * @param {RetryBackoffConfig} [retryConfig]
   * @returns {Observable<T[]>}
   */
  public filterWithRetry(query: object, mode: 'and' | 'or' = 'and', retryConfig?: RetryBackoffConfig): Observable<T[]> {
    /*    console.log('Filter with', query);
    console.log('Filter mode', mode);*/
    let params = '';
    for (const entry of Object.entries(query)) {
      params = params + entry[0] + '=' + entry[1] + (mode === 'and' ? '&' : '|');
    }
    if (params.length !== 0) {
      params = params.substring(0, params.length - 1);
    }

    return this.http.get<T[]>(this.url + '?' + params).pipe(
      retryBackoff({
        ...this.defaultRetryConfig,
        ...retryConfig,
      })
    );
  }

  /**
   * Create a `ServiceAnswer` representing a successful request
   * @param {D} data
   * @returns {ServiceAnswer<D>}
   */
  private answerFromSuccess<D>(data: D): ServiceAnswer<D> {
    const serviceAnswer: ServiceAnswer<D> = {isOk: true, isDataEqual: true};
    serviceAnswer.data = data;
    return serviceAnswer;
  }

  /**
   * Create a `ServiceAnswer` representing a failed request
   * @param {any} error
   * @returns {ServiceAnswer<D>}
   */
  private answerFromError<D>(error: any): ServiceAnswer<D> {
    console.error(`Error when fetching entities from ${this.url}: ${JSON.stringify(error)}`);

    const serviceAnswer: ServiceAnswer<D> = {isOk: false, isDataEqual: true};
    serviceAnswer.errorCode = error && error.status;
    serviceAnswer.errorMessage = (error && error.message) + '. Answer body: ' + JSON.stringify(error && error.error);
    return serviceAnswer;
  }
}
